/**
 * The safety register (MB's decision 837; TJR's Accidents and Landholder pages, TJR inventory 01 §6.3, 02 §7, 03
 * §4.4; docs/specs/safety-register.md): accidents, incidents, near misses and landholder complaints of a project,
 * recorded offline on any of its laptops and shared with Teslyn Cloud (`safety_entry`), with TJR's two forms (Safety
 * Occurrence Notification, Landholder Notification) as PDFs from Teslyn Field and a print view in Teslyn Cloud.
 */
import { jspFileDate, jspPrintedDate } from './jsp-meeting.js';
import type { Project } from './model.js';
import type { ProjectPhoto } from './photos.js';
import { parseTjrIncident, projectIncidents, type Incident } from './incidents.js';
import { jdayToDate } from './dates.js';
import {
  breachHeightWords,
  breachLineText,
  heightBreachEntryLines,
  type HeightBreachEntry,
  type HeightBreachFacts,
  type HeightBreachKind,
} from './height-breach.js';

export const SAFETY_KINDS = ['accident', 'incident', 'near_miss', 'landholder_complaint'] as const;
export type SafetyKind = (typeof SAFETY_KINDS)[number];

export const SAFETY_KIND_WORDS: Readonly<Record<SafetyKind, string>> = {
  accident: 'Accident',
  incident: 'Incident',
  near_miss: 'Near miss',
  landholder_complaint: 'Landholder complaint',
};

/**
 * The register's two kinds filled in from the Height breach report (MB's decision 1021, Q895 A; R205): one record per
 * breach report entry, read from it (`height_breach`), never typed into the register. Not `SAFETY_KINDS`: those are
 * the kinds people record (`safety_entry.kind`, checked by the laptop's database and Teslyn Cloud's).
 */
export const SAFETY_BREACH_KINDS = ['below_lower_working_limit', 'below_hard_deck'] as const;
export type SafetyBreachKind = (typeof SAFETY_BREACH_KINDS)[number];

/** Every kind the register lists, in its order: the four people record, then the two from the breach report. */
export const REGISTER_KINDS = [...SAFETY_KINDS, ...SAFETY_BREACH_KINDS] as const;
export type RegisterKind = (typeof REGISTER_KINDS)[number];

export const REGISTER_KIND_WORDS: Readonly<Record<RegisterKind, string>> = {
  ...SAFETY_KIND_WORDS,
  below_lower_working_limit: 'Below lower working limit',
  below_hard_deck: 'Below hard deck',
};

/** The totals' labels (TJR's "Total Accidents"; the breach kinds as they are). */
export const REGISTER_KIND_TOTAL_WORDS: Readonly<Record<RegisterKind, string>> = {
  accident: 'Accidents',
  incident: 'Incidents',
  near_miss: 'Near misses',
  landholder_complaint: 'Landholder complaints',
  below_lower_working_limit: 'Below lower working limit',
  below_hard_deck: 'Below hard deck',
};

export function isSafetyBreachKind(kind: string): kind is SafetyBreachKind {
  return (SAFETY_BREACH_KINDS as readonly string[]).includes(kind);
}

/** The limits the screen, the laptop's database and Teslyn Cloud all check. */
export const SAFETY_LIMITS = {
  topic: 100,
  name: 200,
  role: 100,
  location: 200,
  landholder_details: 500,
  phone: 50,
  text: 4000,
  people: 40,
  registration: 40,
  /** Project photos linked to one entry (decision 863, 5 B). */
  photos: 20,
} as const;

export interface SafetyPerson {
  /** The staff register person, or null for someone outside it. */
  readonly person_id: string | null;
  readonly name: string;
  readonly role: string;
}

/** An entry as the screen edits it. */
export interface SafetyEntryDraft {
  readonly kind: SafetyKind;
  readonly occurred_on: string;
  readonly occurred_at: string;
  readonly topic: string;
  readonly reported_by_person_id: string | null;
  readonly reported_by_name: string;
  readonly reported_by_role: string;
  readonly people: readonly SafetyPerson[];
  readonly aircraft_id: string | null;
  readonly aircraft_registration: string;
  readonly location: string;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly land_parcel_id: string | null;
  readonly landholder_name: string;
  readonly landholder_details: string;
  readonly landholder_phone: string;
  readonly description: string;
  readonly actions: string;
  readonly corrective_action_taken: boolean;
  readonly follow_up: string;
  readonly follow_up_due: string | null;
  /**
   * The project photos linked to the entry (decision 863, 5 B; `project_photo` ids, in the order chosen, at most
   * {@link SAFETY_LIMITS}.photos). A photo removed later stays linked and shows as removed.
   */
  readonly photo_ids: readonly string[];
}

/** A `safety_entry` row as Teslyn Field and Teslyn Cloud read it. */
export interface SafetyEntry extends SafetyEntryDraft {
  readonly id: string;
  readonly project_id: string;
  readonly closed_at: string | null;
  readonly closed_by_name: string;
  readonly recorded_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

/** Who may record, change, close and remove entries of every kind (TJR inventory 03 §4.4, Safety: E for these four). */
export const SAFETY_RECORD_ROLES = ['tenant_admin', 'manager', 'field_crew', 'data_processor'] as const;

/**
 * Who may record, change, close and remove landholder complaints only (decision 863, 2 B: office staff often take the
 * landholder's phone call). Their other kinds stay read only. Teslyn Cloud's `safety_entry_writer` is this rule.
 */
export const SAFETY_COMPLAINT_ROLES = ['office_staff'] as const;

/**
 * The kinds of entry `roles` may record, change, close, reopen and remove (decisions 837, 863). A laptop not linked to
 * an organisation yet (no roles) records every kind.
 */
export function safetyKindsFor(roles: readonly string[] | null): readonly SafetyKind[] {
  if (roles === null || roles.some((r) => (SAFETY_RECORD_ROLES as readonly string[]).includes(r)))
    return SAFETY_KINDS;
  if (roles.some((r) => (SAFETY_COMPLAINT_ROLES as readonly string[]).includes(r)))
    return ['landholder_complaint'];
  return [];
}

/**
 * Whether one of `roles` records entries of `kind` (and so changes, closes, reopens and removes them), or, with no
 * kind, entries of any kind at all. Changing an entry needs both its old and its new kind.
 */
export function canRecordSafety(roles: readonly string[] | null, kind?: SafetyKind): boolean {
  const kinds = safetyKindsFor(roles);
  return kind === undefined ? kinds.length > 0 : kinds.includes(kind);
}

/** Who records which kinds, in words, for a screen that shows an entry its reader cannot change. */
export const SAFETY_WHO_RECORDS =
  'Tenant admins, managers, field crew and data processors record every kind of entry; office staff record landholder complaints.';

/** A new entry's defaults. */
export function newSafetyEntry(kind: SafetyKind, today: string): SafetyEntryDraft {
  return {
    kind,
    occurred_on: today,
    occurred_at: '',
    topic: '',
    reported_by_person_id: null,
    reported_by_name: '',
    reported_by_role: '',
    people: [],
    aircraft_id: null,
    aircraft_registration: '',
    location: '',
    lat: null,
    lon: null,
    land_parcel_id: null,
    landholder_name: '',
    landholder_details: '',
    landholder_phone: '',
    description: '',
    actions: '',
    corrective_action_taken: false,
    follow_up: '',
    follow_up_due: null,
    photo_ids: [],
  };
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
function validDate(s: string | null): boolean {
  if (!s || !DATE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The words of a kind's action field (TJR: "Suggested action to prevent", "Preventative action"); a breach from the
 * Height breach report carries its reason there (decision 1021).
 */
export function safetyActionWords(kind: RegisterKind): string {
  if (isSafetyBreachKind(kind)) return 'Reason';
  return kind === 'landholder_complaint'
    ? 'Preventative action'
    : 'Suggested action to prevent it happening again';
}

/** What stops an entry being saved, in words (empty when it can be). TJR's checks, without "Duplicated topic". */
export function checkSafetyEntry(e: SafetyEntryDraft): string[] {
  const out: string[] = [];
  const long = (what: string, s: string, max: number): void => {
    if (s.trim().length > max) out.push(`Keep ${what} to ${max} characters.`);
  };
  if (!(SAFETY_KINDS as readonly string[]).includes(e.kind)) out.push('Choose what kind of entry it is.');
  if (!validDate(e.occurred_on)) out.push('Choose the date it happened.');
  if (e.occurred_at.trim() && !TIME.test(e.occurred_at.trim()))
    out.push('Type the time as HH:MM (24 hours), or leave it empty.');
  if (!e.topic.trim()) out.push('Give it a topic.');
  long('the topic', e.topic, SAFETY_LIMITS.topic);
  if (!e.reported_by_name.trim()) out.push('Say who reported it.');
  long('the reporter’s name', e.reported_by_name, SAFETY_LIMITS.name);
  long('the reporter’s role', e.reported_by_role, SAFETY_LIMITS.role);
  if (e.people.length > SAFETY_LIMITS.people) out.push(`List at most ${SAFETY_LIMITS.people} people.`);
  const seen = new Set<string>();
  for (const p of e.people) {
    if (!p.name.trim()) out.push('Every person involved needs a name.');
    if (p.person_id) {
      if (seen.has(p.person_id)) out.push(`${p.name} is listed twice.`);
      seen.add(p.person_id);
    }
    long(`${p.name || 'a person'}’s name`, p.name, SAFETY_LIMITS.name);
  }
  long('the aircraft registration', e.aircraft_registration, SAFETY_LIMITS.registration);
  long('the location', e.location, SAFETY_LIMITS.location);
  if ((e.lat === null) !== (e.lon === null))
    out.push('Give both the latitude and the longitude, or neither.');
  if (e.lat !== null && !(e.lat >= -90 && e.lat <= 90)) out.push('The latitude is −90 to 90 degrees.');
  if (e.lon !== null && !(e.lon >= -180 && e.lon <= 180)) out.push('The longitude is −180 to 180 degrees.');
  if (e.kind === 'landholder_complaint' && !e.landholder_name.trim()) out.push('Name the landholder.');
  long('the landholder’s name', e.landholder_name, SAFETY_LIMITS.name);
  long('the landholder details', e.landholder_details, SAFETY_LIMITS.landholder_details);
  long('the landholder’s phone', e.landholder_phone, SAFETY_LIMITS.phone);
  if (!e.description.trim()) out.push('Describe what happened.');
  long('the description', e.description, SAFETY_LIMITS.text);
  if (!e.actions.trim()) out.push(`Write the ${safetyActionWords(e.kind).toLowerCase()}.`);
  long('the action', e.actions, SAFETY_LIMITS.text);
  long('the follow-up', e.follow_up, SAFETY_LIMITS.text);
  if (e.follow_up_due !== null) {
    if (!validDate(e.follow_up_due)) out.push('The follow-up due date is not a date.');
    else if (validDate(e.occurred_on) && e.follow_up_due < e.occurred_on)
      out.push('The follow-up is due on or after the day it happened.');
  }
  const photos = e.photo_ids ?? [];
  if (photos.length > SAFETY_LIMITS.photos) out.push(`Link at most ${SAFETY_LIMITS.photos} photos.`);
  if (photos.some((id) => !UUID.test(id))) out.push('A linked photo is not one of the project’s photos.');
  if (new Set(photos).size !== photos.length) out.push('A photo is linked twice.');
  return [...new Set(out)];
}

/**
 * TJR's totals (Total Accidents, Total Incidents), with near misses, complaints, the two kinds from the Height breach
 * report (decision 1021) and the entries still open. Removed entries and breaches withdrawn from the breach report
 * are not counted.
 */
export function safetyTotals(
  entries: readonly {
    readonly kind: RegisterKind;
    readonly closed_at: string | null;
    readonly deleted_at: string | null;
  }[],
): Readonly<Record<RegisterKind, number>> & { readonly open: number } {
  const live = entries.filter((e) => !e.deleted_at);
  const n = (k: RegisterKind): number => live.filter((e) => e.kind === k).length;
  return {
    accident: n('accident'),
    incident: n('incident'),
    near_miss: n('near_miss'),
    landholder_complaint: n('landholder_complaint'),
    below_lower_working_limit: n('below_lower_working_limit'),
    below_hard_deck: n('below_hard_deck'),
    open: live.filter((e) => !e.closed_at).length,
  };
}

/** Newest first (date, then time, then when recorded). */
export function safetyOrder(
  a: Pick<SafetyEntry, 'occurred_on' | 'occurred_at' | 'created_at'>,
  b: Pick<SafetyEntry, 'occurred_on' | 'occurred_at' | 'created_at'>,
): number {
  return (
    b.occurred_on.localeCompare(a.occurred_on) ||
    b.occurred_at.localeCompare(a.occurred_at) ||
    b.created_at.localeCompare(a.created_at)
  );
}

/** Whether a follow-up is overdue on `today` (an open entry whose due date has passed). */
export function safetyOverdue(e: Pick<SafetyEntry, 'closed_at' | 'follow_up_due'>, today: string): boolean {
  return !e.closed_at && !!e.follow_up_due && e.follow_up_due < today;
}

// ------------------------------------------------------------------------------------------------ from the TJR job

/** The key of a TJR record (so each is brought in once): its topic, date and time as `parseTjrIncident` reads them. */
export function tjrSafetyKey(i: Pick<Incident, 'topic' | 'date' | 'time'>): string {
  return [i.topic, i.date, i.time]
    .map((x) => x.trim())
    .filter(Boolean)
    .join(' ');
}

/** The line an entry brought in from TJR carries in its follow-up, naming the record. */
const TJR_MARK = /From the project file \(record (300|302): ([^)]*)\)\./;

/** The TJR records already in the register (`300|<key>`, `302|<key>`). */
function tjrMarks(existing: readonly Pick<SafetyEntry, 'follow_up' | 'deleted_at'>[]): Set<string> {
  return new Set(
    existing
      .map((e) => TJR_MARK.exec(e.follow_up))
      .filter((m): m is RegExpExecArray => !!m)
      .map((m) => `${m[1]}|${m[2]}`),
  );
}

/**
 * Entries for TJR's records 300 (accidents and incidents) and 302 (landholder complaints) of an imported job
 * (`project.legacy_json.unmodelled`; TJF spec §3.8) that are not in the register yet. Each new entry's follow-up says
 * which record it came from, so it is never brought in twice.
 */
export function safetyEntriesFromTjr(
  project: Pick<Project, 'legacy_json' | 'number'>,
  existing: readonly Pick<SafetyEntry, 'follow_up' | 'deleted_at'>[],
): SafetyEntryDraft[] {
  const raw = (project.legacy_json ?? {})['unmodelled'];
  if (!Array.isArray(raw)) return [];
  const have = tjrMarks(existing);
  const out: SafetyEntryDraft[] = [];
  for (const r of raw) {
    if (!Array.isArray(r) || (r[0] !== '300' && r[0] !== '302') || typeof r[1] !== 'string') continue;
    const code = r[0] as '300' | '302';
    const f = (r[1] as string).split('~');
    const inc = parseTjrIncident(r[1] as string);
    if (!inc.date) continue;
    const key = tjrSafetyKey(inc);
    if (have.has(`${code}|${key}`)) continue;
    have.add(`${code}|${key}`);
    out.push({
      ...newSafetyEntry(code === '302' ? 'landholder_complaint' : inc.kind, inc.date),
      occurred_at: TIME.test(inc.time) ? inc.time : '',
      topic: inc.topic.slice(0, SAFETY_LIMITS.topic) || 'From the project file',
      reported_by_name: inc.reported_by || 'Not recorded in the project file',
      reported_by_role: inc.role,
      location: inc.location || project.number,
      landholder_name: code === '302' ? (f[9] ?? '').trim() || 'Not recorded in the project file' : '',
      landholder_details: code === '302' ? (f[10] ?? '').trim() : '',
      landholder_phone: code === '302' ? (f[11] ?? '').trim() : '',
      description: inc.description || inc.topic,
      actions: inc.prevention || 'Not recorded in the project file.',
      corrective_action_taken: (f[12] ?? '').trim() === '-1',
      follow_up: `From the project file (record ${code}: ${key}).`,
    });
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ the forms

export interface SafetyFormContent {
  readonly title: 'Safety Occurrence Notification' | 'Landholder Notification';
  /** The label and value pairs at the top, in TJR's order, then Teslyn's own lines. */
  readonly fields: readonly (readonly [string, string])[];
  readonly description_heading: string;
  readonly description: string;
  readonly action_heading: string;
  readonly action: string;
  /** Teslyn's lines below TJR's (people, aircraft, place, follow-up, closed), only those filled. */
  readonly extra: readonly (readonly [string, string])[];
  /** TJR's "Office Use Only" box, printed empty for paper. */
  readonly office: readonly string[];
  readonly office_signature: readonly string[];
}

/** A project photo as the register names it. */
export type SafetyPhoto = Pick<ProjectPhoto, 'id' | 'file_name' | 'caption' | 'deleted_at'>;

/**
 * The names of an entry's linked photos, in the order linked (decision 863, 5 B): the file name, with the caption in
 * brackets; a photo removed from the project says so, and one this laptop or page has not got yet is counted.
 * TJR's forms are one page with no room for pictures, so the forms list these names.
 */
export function safetyPhotoNames(
  photoIds: readonly string[] | undefined,
  photos: readonly SafetyPhoto[],
): string[] {
  const out: string[] = [];
  let unknown = 0;
  for (const id of photoIds ?? []) {
    const p = photos.find((x) => x.id === id);
    if (!p) {
      unknown += 1;
      continue;
    }
    const caption = p.caption.trim();
    out.push(
      `${p.file_name}${caption ? ` (${caption})` : ''}${p.deleted_at ? ', removed from the project' : ''}`,
    );
  }
  if (unknown > 0)
    out.push(unknown === 1 ? 'one photo not synced here yet' : `${unknown} photos not synced here yet`);
  return out;
}

const NOUN: Readonly<Record<Exclude<SafetyKind, 'landholder_complaint'>, string>> = {
  accident: 'Accident',
  incident: 'Incident',
  near_miss: 'Near Miss',
};

/** What a form is made of: an entry (or its draft), or a register record from the Height breach report. */
export type SafetyFormInput = Omit<SafetyEntryDraft, 'kind'> & {
  readonly kind: RegisterKind;
  readonly from_breach?: RegisterBreachLink | null;
} & Partial<Pick<SafetyEntry, 'closed_at' | 'closed_by_name'>>;

/**
 * The words of an entry's form (`airptfm.scp`, `cmrptfm.scp`), for the PDF and Teslyn Cloud's print view. `photos`
 * are the project's photos, to name the linked ones (decision 863, 5 B). A breach from the Height breach report
 * (decision 1021) prints as a Safety Occurrence Notification of a height breach: its facts as the description, its
 * reason as the action.
 */
export function safetyFormContent(
  e: SafetyFormInput,
  photos: readonly SafetyPhoto[] = [],
): SafetyFormContent {
  if (isSafetyBreachKind(e.kind)) return breachFormContent(e);
  const complaint = e.kind === 'landholder_complaint';
  const fields: [string, string][] = [
    ['Location or Job Number', e.location.trim()],
    ['Name', e.reported_by_name.trim()],
    ['Role', e.reported_by_role.trim()],
    ['Date', validDate(e.occurred_on) ? jspPrintedDate(e.occurred_on) : ''],
    ['Time', e.occurred_at.trim()],
  ];
  if (complaint)
    fields.push(
      ['Landholder', e.landholder_name.trim()],
      ['Phone', e.landholder_phone.trim()],
      ['Details', e.landholder_details.trim()],
    );
  else fields.push(['Topic', e.topic.trim()]);
  const extra: [string, string][] = [];
  if (complaint) extra.push(['Topic', e.topic.trim()]);
  if (e.people.length)
    extra.push([
      'People involved',
      e.people.map((p) => (p.role ? `${p.name} (${p.role})` : p.name)).join(', '),
    ]);
  if (e.aircraft_registration.trim()) extra.push(['Aircraft', e.aircraft_registration.trim()]);
  if (e.lat !== null && e.lon !== null)
    extra.push(['Place on the map', `${e.lat.toFixed(5)}, ${e.lon.toFixed(5)} (WGS 84)`]);
  extra.push(['Corrective action has been taken', e.corrective_action_taken ? 'Yes' : 'No']);
  if (e.follow_up.trim()) extra.push(['Follow-up', e.follow_up.trim()]);
  if (e.follow_up_due && validDate(e.follow_up_due))
    extra.push(['Follow-up due', jspPrintedDate(e.follow_up_due)]);
  const named = safetyPhotoNames(e.photo_ids, photos);
  if (named.length) extra.push(['Photos', named.join('; ')]);
  if (e.closed_at) {
    const d = e.closed_at.slice(0, 10);
    extra.push([
      'Closed',
      [validDate(d) ? jspPrintedDate(d) : '', e.closed_by_name ?? ''].filter(Boolean).join(' by '),
    ]);
  }
  const noun = complaint ? '' : NOUN[e.kind as Exclude<SafetyKind, 'landholder_complaint'>];
  return {
    title: complaint ? 'Landholder Notification' : 'Safety Occurrence Notification',
    fields,
    description_heading: complaint ? 'Description:' : `Description of ${noun}:`,
    description: e.description.trim(),
    action_heading: complaint ? 'Preventative Action:' : `Suggested Action To Prevent Future ${noun}:`,
    action: e.actions.trim(),
    extra,
    office: OFFICE,
    office_signature: OFFICE_SIGNATURE,
  };
}

const OFFICE = ['Incident Number', 'Corrective Action Required:', 'Action Taken:'] as const;
const OFFICE_SIGNATURE = ['Date', 'Signed', 'Name', 'Position'] as const;

/** A breach's Safety Occurrence Notification (decision 1021): TJR's fields, the facts, the reason, where it is from. */
function breachFormContent(e: SafetyFormInput): SafetyFormContent {
  const b = e.from_breach ?? null;
  const fields: [string, string][] = [
    ['Location or Job Number', e.location.trim()],
    ['Name', e.reported_by_name.trim()],
    ['Role', e.reported_by_role.trim()],
    ['Date', validDate(e.occurred_on) ? jspPrintedDate(e.occurred_on) : ''],
    ['Time', e.occurred_at.trim()],
    ['Topic', e.topic.trim()],
  ];
  const extra: [string, string][] = [];
  if (b) {
    extra.push(['Below', breachHeightWords(b.test_key, b.facts.threshold_m)]);
    extra.push(['Recorded in', 'The height breach report (Teslyn Field, Flight data QC)']);
    if (b.reason_changes > 0)
      extra.push([
        'Reason changed',
        b.reason_changes === 1
          ? 'once in the breach report'
          : `${b.reason_changes} times in the breach report`,
      ]);
    if (b.withdrawn) extra.push(['Withdrawn from the breach report', breachWithdrawnText(b.withdrawn)]);
  }
  if (e.closed_at) {
    const d = e.closed_at.slice(0, 10);
    extra.push([
      'Reason recorded',
      [validDate(d) ? jspPrintedDate(d) : '', e.closed_by_name ?? ''].filter(Boolean).join(' by '),
    ]);
  }
  return {
    title: 'Safety Occurrence Notification',
    fields,
    description_heading: 'Description of Height Breach:',
    description: e.description.trim(),
    action_heading: 'Reason:',
    action: e.actions.trim(),
    extra,
    office: OFFICE,
    office_signature: OFFICE_SIGNATURE,
  };
}

/**
 * The form's file name in `Documents\Safety Register\`:
 * `<project folder>_<Safety_Occurrence|Landholder_Notification>_<d-Month-yyyy>_<topic>.pdf`.
 */
export function safetyFormFileName(
  folderName: string,
  e: { readonly kind: RegisterKind; readonly occurred_on: string; readonly topic: string },
): string {
  const safe = (s: string): string =>
    s
      .replace(/[\\/:*?"<>|\s]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  const base = safe(folderName) || 'Project';
  const form = e.kind === 'landholder_complaint' ? 'Landholder_Notification' : 'Safety_Occurrence';
  const topic = safe(e.topic).slice(0, 40).replace(/_$/, '');
  return `${base}_${form}_${jspFileDate(e.occurred_on)}${topic ? `_${topic}` : ''}.pdf`;
}

export const SAFETY_FORM_SCHEMA = 'teslyn.safety-form.v1';

export interface SafetyFormManifest extends SafetyFormContent {
  readonly schema: typeof SAFETY_FORM_SCHEMA;
  readonly tenant: { readonly key: string; readonly name: string; readonly brand: string };
  readonly kind_words: string;
  readonly footer_text: string;
}

/** The manifest `report::safety_form` renders (mirrored with serde in `crates/teslyn-core/src/report/safety_form.rs`). */
export function safetyFormManifest(input: {
  readonly tenant: SafetyFormManifest['tenant'];
  readonly project: Pick<Project, 'number' | 'client_name'>;
  readonly entry: SafetyFormInput;
  /** The project's photos, to name the entry's linked ones. */
  readonly photos?: readonly SafetyPhoto[];
}): SafetyFormManifest {
  return {
    schema: SAFETY_FORM_SCHEMA,
    tenant: input.tenant,
    kind_words: REGISTER_KIND_WORDS[input.entry.kind],
    ...safetyFormContent(input.entry, input.photos ?? []),
    footer_text: `${input.project.number} ${input.project.client_name}`.trim(),
  };
}

// ------------------------------------------------------------------------------------------------ height breaches

/**
 * The register reads the Height breach report (MB's decision 1021, Q895 A; R205; docs/specs/safety-register.md §9):
 * each breach report entry (`height_breach`, R196, R197) is one register record of kind "Below lower working limit"
 * or "Below hard deck", made here from the entry each time the register is read, never stored as a second copy. Its
 * reason and its removal are the entry's: changed or removed in the breach report, the register follows; nobody types
 * it into the register, and the register does not change it.
 */
export const BREACH_RECORD_SOURCE_WORDS = 'From the height breach report';

/** What the register says of a record it cannot change (Teslyn Field and Teslyn Cloud). */
export const BREACH_RECORD_READ_ONLY =
  'Recorded once, in the height breach report (Teslyn Field, Flight data QC, Breach report): change its reason or remove it there, and the register follows.';

/** A breach removed from the breach report: when, by whom and why. */
export interface RegisterBreachWithdrawal {
  readonly at: string;
  readonly by_name: string;
  readonly reason: string;
}

/** A register record's link to its Height breach report entry, with what the register shows of it. */
export interface RegisterBreachLink {
  readonly breach_id: string;
  readonly run_id: string;
  /** The Flight data QC line result the breach was found on (Teslyn Field opens its Breach report there). */
  readonly result_id: string;
  readonly flight_id: string | null;
  readonly test_key: HeightBreachKind;
  readonly facts: HeightBreachFacts;
  readonly reason: string;
  /** How many times the reason was changed in the breach report. */
  readonly reason_changes: number;
  readonly withdrawn: RegisterBreachWithdrawal | null;
}

/**
 * A record of the register: an entry people record (`safety_entry`), or a breach read from the Height breach report
 * (`from_breach` set). Every column the register shows is filled for both, so lists, filters, totals and forms read
 * them alike.
 */
export interface RegisterRecord extends Omit<SafetyEntry, 'kind'> {
  readonly kind: RegisterKind;
  readonly from_breach?: RegisterBreachLink | null;
}

export function registerKindOfBreach(test: HeightBreachKind): SafetyBreachKind {
  return test === 'hard_deck' ? 'below_hard_deck' : 'below_lower_working_limit';
}

/**
 * The date of a breach (yyyy-mm-dd): its survey day (`jday`) in the year it was added to the report. A day more than
 * two days after the entry was added is last year's (a breach of 31 December added on 2 January); the two days allow
 * for the survey's own clock ahead of UTC.
 */
export function breachOccurredOn(jday: number, addedAt: string): string {
  const added = addedAt.slice(0, 10);
  const year = Number(added.slice(0, 4));
  const on = (y: number): string => jdayToDate(y, jday).toISOString().slice(0, 10);
  const limit = new Date(Date.parse(`${added}T00:00:00Z`) + 2 * 86_400_000).toISOString().slice(0, 10);
  return on(year) > limit ? on(year - 1) : on(year);
}

const pad3 = (n: number): string => String(n).padStart(3, '0');

/** The record of one breach report entry (decision 1021): its facts, reason and removal, as the register shows them. */
export function registerRecordFromBreach(b: HeightBreachEntry): RegisterRecord {
  const f = b.facts;
  const withdrawn: RegisterBreachWithdrawal | null = b.deleted_at
    ? { at: b.deleted_at, by_name: b.deleted_by_name ?? '', reason: b.deleted_reason ?? '' }
    : null;
  const history = Array.isArray(b.history) ? b.history : [];
  return {
    id: b.id,
    project_id: b.project_id,
    kind: registerKindOfBreach(b.test_key),
    occurred_on: breachOccurredOn(f.jday, b.created_at),
    occurred_at: f.local_time ?? '',
    topic: `Day ${f.jday} Flt${pad3(f.flight_number)}, Line ${breachLineText(f.line_name)}`,
    reported_by_person_id: null,
    reported_by_name: b.created_by_name || 'A laptop of the project',
    reported_by_role: '',
    people: [],
    aircraft_id: null,
    aircraft_registration: '',
    location:
      f.east !== null && f.north !== null ? `East ${f.east.toFixed(2)} North ${f.north.toFixed(2)}` : '',
    lat: null,
    lon: null,
    land_parcel_id: null,
    landholder_name: '',
    landholder_details: '',
    landholder_phone: '',
    description: heightBreachEntryLines(f).join('\n'),
    actions: b.reason.trim(),
    corrective_action_taken: false,
    follow_up: '',
    follow_up_due: null,
    photo_ids: [],
    // the reason is required when a breach is added to the report, so its record is closed from then on
    closed_at: b.created_at,
    closed_by_name: b.created_by_name,
    recorded_by_name: b.created_by_name,
    created_at: b.created_at,
    updated_at: b.updated_at,
    deleted_at: b.deleted_at,
    from_breach: {
      breach_id: b.id,
      run_id: b.run_id,
      result_id: b.result_id,
      flight_id: b.flight_id,
      test_key: b.test_key,
      facts: f,
      reason: b.reason.trim(),
      reason_changes: history.length,
      withdrawn,
    },
  };
}

/**
 * The register as it is read (decision 1021): the live entries people recorded and every breach of the Height breach
 * report (those withdrawn from it too, marked so), newest first.
 */
export function safetyRegisterRecords(
  entries: readonly SafetyEntry[],
  breaches: readonly HeightBreachEntry[],
): RegisterRecord[] {
  return [
    ...entries.filter((e) => !e.deleted_at).map((e): RegisterRecord => ({ ...e, from_breach: null })),
    ...breaches.map(registerRecordFromBreach),
  ].sort(safetyOrder);
}

/** A record the register's own forms change (an entry people recorded), else null (a breach: read only here). */
export function registerSafetyEntry(r: RegisterRecord): SafetyEntry | null {
  return r.from_breach || isSafetyBreachKind(r.kind) ? null : (r as SafetyEntry);
}

/** Whether `roles` may change, close, reopen and remove the record in the register (never a breach's). */
export function canChangeRegisterRecord(roles: readonly string[] | null, r: RegisterRecord): boolean {
  const e = registerSafetyEntry(r);
  return e !== null && canRecordSafety(roles, e.kind);
}

/** `04/11/2026 by Tim: Entered twice` */
export function breachWithdrawnText(w: RegisterBreachWithdrawal): string {
  const d = w.at.slice(0, 10);
  const when = [validDate(d) ? jspPrintedDate(d) : '', w.by_name.trim()].filter(Boolean).join(' by ');
  return w.reason.trim() ? `${when}: ${w.reason.trim()}` : when;
}

/**
 * A record's state in the register: open (follow-up overdue or not), closed, or withdrawn from the breach report. A
 * breach is closed once its reason is recorded, which is when it is added to the report (decision 1021).
 */
export type RegisterState = 'open' | 'overdue' | 'closed' | 'withdrawn';

export function registerRecordState(
  r: Pick<RegisterRecord, 'closed_at' | 'follow_up_due' | 'deleted_at'>,
  today: string,
): RegisterState {
  if (r.deleted_at) return 'withdrawn';
  if (r.closed_at) return 'closed';
  return safetyOverdue(r, today) ? 'overdue' : 'open';
}

/** The register's "Show" filter: open and closed (with those withdrawn), open, closed, or withdrawn only. */
export const REGISTER_SHOW = ['all', 'open', 'closed', 'withdrawn'] as const;
export type RegisterShow = (typeof REGISTER_SHOW)[number];

export const REGISTER_SHOW_WORDS: Readonly<Record<RegisterShow, string>> = {
  all: 'Open and closed',
  open: 'Open',
  closed: 'Closed',
  withdrawn: 'Withdrawn from the breach report',
};

/** Whether a record passes the register's filters (project, kind, show). */
export function registerRecordShown(
  r: Pick<RegisterRecord, 'project_id' | 'kind' | 'closed_at' | 'deleted_at'>,
  filter: { readonly project?: string; readonly kind?: RegisterKind | 'all'; readonly show?: RegisterShow },
): boolean {
  if (filter.project && filter.project !== 'all' && r.project_id !== filter.project) return false;
  if (filter.kind && filter.kind !== 'all' && r.kind !== filter.kind) return false;
  const state = r.deleted_at ? 'withdrawn' : r.closed_at ? 'closed' : 'open';
  const show = filter.show ?? 'all';
  return show === 'all' || show === state;
}

// ------------------------------------------------------------------------------------------------ the daily report

/**
 * The register's accidents, incidents and near misses up to `toDate` (inclusive) for the daily report's "Print
 * accidents/incidents" (decision 145), oldest first, in the shape of TJR's own records (`./incidents.ts`).
 */
export function safetyIncidentsForReport(
  entries: readonly SafetyEntry[],
  toDate: string,
): {
  readonly date: string;
  readonly time: string;
  readonly kind: 'accident' | 'incident' | 'near_miss';
  readonly topic: string;
  readonly description: string;
  readonly prevention: string;
  readonly reported_by: string;
  readonly role: string;
  readonly location: string;
}[] {
  return entries
    .filter((e) => !e.deleted_at && e.kind !== 'landholder_complaint' && e.occurred_on <= toDate)
    .sort((a, b) => a.occurred_on.localeCompare(b.occurred_on) || a.occurred_at.localeCompare(b.occurred_at))
    .map((e) => ({
      date: e.occurred_on,
      time: e.occurred_at,
      kind: e.kind as 'accident' | 'incident' | 'near_miss',
      topic: e.topic,
      description: e.description,
      prevention: e.actions,
      reported_by: e.reported_by_name,
      role: e.reported_by_role,
      location: e.location,
    }));
}

/** A row of the daily report's "Accidents and Incidents" (the kind in words). */
export interface ReportIncident {
  readonly date: string;
  readonly time: string;
  readonly kind: string;
  readonly topic: string;
  readonly description: string;
  readonly prevention: string;
  readonly reported_by: string;
  readonly role: string;
  readonly location: string;
}

/**
 * Everything the daily report's "Print accidents/incidents" prints to `toDate`: TJR's own records 300 of an imported
 * job that were not brought into the register, and the register's accidents, incidents and near misses (so none
 * prints twice), oldest first.
 */
export function reportIncidents(
  project: Pick<Project, 'legacy_json'>,
  entries: readonly SafetyEntry[],
  toDate: string,
): ReportIncident[] {
  const have = tjrMarks(entries.filter((e) => !e.deleted_at));
  const legacy: ReportIncident[] = projectIncidents(project, toDate)
    .filter((i) => !have.has(`300|${tjrSafetyKey(i)}`))
    .map((i) => ({ ...i, kind: i.kind === 'accident' ? 'Accident' : 'Incident' }));
  const register: ReportIncident[] = safetyIncidentsForReport(entries, toDate).map((i) => ({
    ...i,
    kind: SAFETY_KIND_WORDS[i.kind],
  }));
  return [...legacy, ...register].sort(
    (a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time),
  );
}
